import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Megaphone, Plus, Upload, Play, X, RefreshCw, BarChart2, Users,
} from 'lucide-react';

const STATUS_BADGE = {
  draft: 'bg-slate-50 text-slate-600 border-slate-200',
  scheduled: 'bg-amber-50 text-amber-700 border-amber-200',
  running: 'bg-blue-50 text-blue-700 border-blue-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-rose-50 text-rose-700 border-rose-200',
};

// Extract {{1}} {{2}} … placeholders from a template's components.
function extractTemplateVars(template) {
  if (!template?.components) return { count: 0, bodyText: '', headerText: '' };
  const vars = new Set();
  let bodyText = '';
  let headerText = '';
  for (const c of template.components) {
    const text = c?.text || '';
    if (c?.type === 'BODY') bodyText = text;
    if (c?.type === 'HEADER') headerText = text;
    const matches = text.match(/{{\s*(\d+)\s*}}/g) || [];
    for (const m of matches) {
      const num = parseInt(m.replace(/[^0-9]/g, ''), 10);
      if (!Number.isNaN(num)) vars.add(num);
    }
  }
  return {
    count: vars.size,
    bodyText,
    headerText,
    indices: [...vars].sort((a, b) => a - b),
  };
}

function fillPreview(text, values) {
  if (!text) return '';
  return text.replace(/{{\s*(\d+)\s*}}/g, (_, n) => {
    const idx = parseInt(n, 10) - 1;
    const v = values[idx];
    return v ? v : `{{${n}}}`;
  });
}

function CreateCampaignDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [language, setLanguage] = useState('en_US');
  const [rate, setRate] = useState(60);
  const [templates, setTemplates] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.name === templateName),
    [templates, templateName]
  );
  const { count: varCount, bodyText, headerText } = useMemo(
    () => extractTemplateVars(selectedTemplate),
    [selectedTemplate]
  );

  // Sample values for live preview (NOT submitted — campaign uses CSV/segment vars)
  const [sampleVals, setSampleVals] = useState([]);
  useEffect(() => {
    setSampleVals(Array(varCount).fill(''));
  }, [varCount]);

  useEffect(() => {
    if (open) {
      api
        .get('/api/templates/approved')
        .then((r) => {
          setTemplates(r.templates || []);
          // auto-pick template language when one is chosen
        })
        .catch(() => {});
    }
  }, [open]);

  // Auto-sync language to selected template
  useEffect(() => {
    if (selectedTemplate?.language) setLanguage(selectedTemplate.language);
  }, [selectedTemplate]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await api.post('/api/campaigns', {
        name,
        template_name: templateName,
        language,
        rate_per_minute: rate,
      });
      toast.success('Campaign created');
      onCreated?.();
      setOpen(false);
      setName('');
      setTemplateName('');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="create-campaign-btn" className="bg-[#00D1B2] hover:bg-[#00B89C] text-white gap-2">
          <Plus className="w-4 h-4" /> New Campaign
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>Create Broadcast Campaign</DialogTitle></DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div>
            <Label>Name</Label>
            <Input data-testid="camp-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Diwali promo" />
          </div>
          <div>
            <Label>Approved Template</Label>
            {templates.length === 0 ? (
              <Input
                data-testid="camp-template-input"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="hello_world (no approved templates yet — type one)"
              />
            ) : (
              <Select value={templateName} onValueChange={setTemplateName}>
                <SelectTrigger data-testid="camp-template-select"><SelectValue placeholder="Pick a template" /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.name}>
                      {t.name} <span className="text-xs text-slate-400">· {t.language}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Variable count + live preview */}
          {selectedTemplate && (
            <div className="rounded-lg border border-gray-100 bg-slate-50 p-3 space-y-2" data-testid="camp-template-preview">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Template preview</p>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#00D1B2]/10 text-[#00D1B2] font-semibold">
                  {varCount} variable{varCount === 1 ? '' : 's'}
                </span>
              </div>
              {headerText && (
                <p className="text-[11px] text-slate-500"><span className="font-semibold">Header:</span> {fillPreview(headerText, sampleVals)}</p>
              )}
              <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed bg-white rounded p-2 border border-gray-100">
                {fillPreview(bodyText, sampleVals) || <span className="text-slate-400">No body text</span>}
              </p>
              {varCount > 0 && (
                <div className="grid grid-cols-2 gap-2 pt-1">
                  {Array.from({ length: varCount }).map((_, i) => (
                    <Input
                      key={i}
                      data-testid={`camp-sample-var-${i + 1}`}
                      placeholder={`var_${i + 1} (sample)`}
                      value={sampleVals[i] || ''}
                      onChange={(e) => {
                        const next = [...sampleVals];
                        next[i] = e.target.value;
                        setSampleVals(next);
                      }}
                      className="text-xs h-8"
                    />
                  ))}
                </div>
              )}
              <p className="text-[10px] text-slate-400">
                Sample values are for preview only. Real values come from your CSV columns
                (<span className="font-mono">phone, var_1, var_2…</span>) or segment-default values when launching.
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Language</Label>
              <Input data-testid="camp-language-input" value={language} onChange={(e) => setLanguage(e.target.value)} />
            </div>
            <div>
              <Label>Rate / min</Label>
              <Input type="number" value={rate} onChange={(e) => setRate(parseInt(e.target.value || '60', 10))} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting || !name || !templateName} className="bg-[#00D1B2] hover:bg-[#00B89C] text-white">
            {submitting ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EnrollFromSegmentDialog({ campaign, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [segments, setSegments] = useState([]);
  const [segmentId, setSegmentId] = useState('');
  const [defaultVars, setDefaultVars] = useState('');
  const [resolved, setResolved] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      api.get('/api/segments').then((r) => setSegments(r.segments || [])).catch(() => {});
    } else {
      setSegmentId('');
      setDefaultVars('');
      setResolved(null);
    }
  }, [open]);

  const previewSegment = async (id) => {
    if (!id) return setResolved(null);
    try {
      const r = await api.get(`/api/segments/${id}/resolve`);
      setResolved(r);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const default_var_values = defaultVars
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const r = await api.post(`/api/campaigns/${campaign.id}/enroll-from-segment`, {
        segment_id: segmentId,
        default_var_values,
      });
      if (r.ok === false) {
        toast.error(r.errors?.join(', ') || 'Enrollment failed');
      } else {
        toast.success(`Queued ${r.queued || 0} contacts${r.skipped ? `, skipped ${r.skipped}` : ''}`);
      }
      onUpdated?.();
      setOpen(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid={`camp-enroll-segment-${campaign.id}`}
          className="cursor-pointer inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border border-gray-200 hover:bg-slate-50"
        >
          <Users className="w-3 h-3" /> Use Segment
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Enroll audience from a segment</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Segment</Label>
            <Select
              value={segmentId}
              onValueChange={(v) => {
                setSegmentId(v);
                previewSegment(v);
              }}
            >
              <SelectTrigger data-testid="camp-segment-select">
                <SelectValue placeholder={segments.length === 0 ? 'No segments yet — create one in Audience Segments' : 'Pick a segment'} />
              </SelectTrigger>
              <SelectContent>
                {segments.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Default variable values (comma-separated)</Label>
            <Input
              data-testid="camp-segment-vars"
              placeholder="Customer, 10%"
              value={defaultVars}
              onChange={(e) => setDefaultVars(e.target.value)}
            />
            <p className="text-[10px] text-slate-400 mt-1">
              These values are reused for every recipient (var_1, var_2…). Leave empty for templates with no variables.
            </p>
          </div>
          {resolved && (
            <div className="bg-slate-50 rounded-md p-3 text-xs" data-testid="camp-segment-preview">
              <p className="font-semibold text-slate-700">{resolved.size} contacts will be queued</p>
              {resolved.phones?.length > 0 && (
                <p className="mt-1 text-slate-500 font-mono break-all">
                  {resolved.phones.slice(0, 5).join(', ')}{resolved.phones.length > 5 ? '…' : ''}
                </p>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            data-testid="camp-segment-submit-btn"
            onClick={submit}
            disabled={busy || !segmentId}
            className="bg-[#00D1B2] hover:bg-[#00B89C] text-white"
          >
            {busy ? 'Enrolling…' : 'Enroll audience'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CampaignActions({ campaign, onUpdated }) {
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.upload(`/api/campaigns/${campaign.id}/upload-audience`, fd);
      toast.success(`Audience uploaded: ${r.queued} queued${r.skipped ? `, ${r.skipped} skipped` : ''}`);
      onUpdated?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const launch = async () => {
    setBusy(true);
    try {
      await api.post(`/api/campaigns/${campaign.id}/launch`);
      toast.success('Campaign launched');
      onUpdated?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm('Cancel this campaign?')) return;
    setBusy(true);
    try {
      await api.post(`/api/campaigns/${campaign.id}/cancel`);
      toast.success('Cancelled');
      onUpdated?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2 shrink-0">
      {campaign.status === 'draft' && (
        <>
          <label
            data-testid={`camp-upload-${campaign.id}`}
            className="cursor-pointer inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border border-gray-200 hover:bg-slate-50"
          >
            <Upload className="w-3 h-3" />
            {uploading ? 'Uploading…' : 'Upload CSV'}
            <input type="file" accept=".csv" onChange={handleUpload} className="hidden" />
          </label>
          <EnrollFromSegmentDialog campaign={campaign} onUpdated={onUpdated} />
          <Button
            size="sm"
            data-testid={`camp-launch-${campaign.id}`}
            disabled={busy || campaign.audience_count === 0}
            onClick={launch}
            className="bg-[#00D95F] hover:bg-[#00C054] text-white gap-1.5"
          >
            <Play className="w-3 h-3" /> Launch
          </Button>
        </>
      )}
      {(campaign.status === 'running' || campaign.status === 'scheduled') && (
        <Button size="sm" variant="ghost" disabled={busy} onClick={cancel} className="text-rose-600 gap-1.5">
          <X className="w-3 h-3" /> Cancel
        </Button>
      )}
    </div>
  );
}

export function CampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/campaigns');
      setCampaigns(res.campaigns || []);
    } catch (e) {
      toast.error(`Could not load campaigns: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000); // poll while running
    return () => clearInterval(t);
  }, []);

  return (
    <div className="space-y-8" data-testid="campaigns-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Campaigns</h2>
          <p className="text-sm text-slate-500 mt-1">
            Broadcast approved templates to a CSV audience or saved segment with per-minute rate limits.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" onClick={load} data-testid="camp-refresh-btn" className="gap-2">
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
          <CreateCampaignDialog onCreated={load} />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid="campaigns-list">
        {loading ? (
          <div className="p-12 text-center text-sm text-slate-400">Loading…</div>
        ) : campaigns.length === 0 ? (
          <div className="p-16 text-center">
            <Megaphone className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <p className="text-sm font-medium text-slate-700">No campaigns yet</p>
            <p className="text-xs text-slate-400 mt-1">CSV format: phone,var_1,var_2 …  · Or pick a saved segment.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {campaigns.map((c) => (
              <div key={c.id} data-testid={`camp-row-${c.id}`} className="flex items-start justify-between gap-4 p-5 hover:bg-slate-50/50">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="font-semibold text-slate-900 text-sm">{c.name}</p>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${STATUS_BADGE[c.status] || STATUS_BADGE.draft}`}>
                      {c.status}
                    </span>
                    <span className="text-[11px] text-slate-400 font-mono">{c.template_name}</span>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <BarChart2 className="w-3 h-3" />
                      {c.sent_count}/{c.audience_count} sent
                    </span>
                    {c.failed_count > 0 && (
                      <span className="text-rose-500">{c.failed_count} failed</span>
                    )}
                    <span>· {c.rate_per_minute}/min</span>
                  </div>
                </div>
                <CampaignActions campaign={c} onUpdated={load} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
